import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Info as InfoIcon,
  Timeline as TimelineIcon,
  PhotoCamera as PhotoCameraIcon,
  Link as LinkIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';

interface BaselineAnnotationGuideProps {
  isFollowupSession: boolean;
  hasBaselineSessions: boolean;
}

const BaselineAnnotationGuide: React.FC<BaselineAnnotationGuideProps> = ({
  isFollowupSession,
  hasBaselineSessions
}) => {
  // Only show if there are baseline sessions available
  if (!hasBaselineSessions) {
    return null;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <InfoIcon color="info" />
          <Typography variant="h6" color="info.main">
            Baseline Comparison Available
          </Typography>
        </Box>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          {hasBaselineSessions 
            ? "Baseline sessions are available. Use the Baseline Comparison tab to select baseline annotations and add follow-up dermatoscopic images for longitudinal tracking."
            : "This session contains dermatoscopic images and can serve as a baseline for future comparisons. Use the Baseline Comparison tab to manage follow-up tracking."
          }
        </Alert>

        <Typography variant="subtitle2" gutterBottom>
          How to add follow-up images:
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CompareArrowsIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="1. Go to Baseline Comparison tab"
              secondary="View all baseline sessions and their annotations"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <LinkIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="2. Select a baseline annotation"
              secondary="Click on any baseline annotation you want to add follow-up images to"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <PhotoCameraIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="3. Add dermatoscopic images"
              secondary="Upload or capture new dermatoscopic images for comparison"
            />
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );
};

export default BaselineAnnotationGuide;